import React, { useMemo, useEffect } from 'react';
import { useScrollTop } from 'Hooks/useScrollTop';
import { useEmailCoupon } from 'Hooks/useEmailCoupon';
import Header from 'Layouts/Header';
import { Footer } from 'Layouts/Footer';
import { DocHead } from 'Components/DocHead/DocHead';
import { ErrorBoundary } from 'Components/ErrorBoundary/ErrorBoundary';
import { identify, loadTealium } from 'Utils/Analytics';
import { heapIdentify } from 'Utils/Analytics/Heap';
import { setBrazeUser } from 'Utils/Braze';
import { useLocation, matchPath } from 'react-router-dom';
import Overlay from 'Layouts/Overlay';
import { loadPaypalSDK } from 'Api/PayPal';
import { usePlans } from 'Hooks/usePlans';
import { useStore } from 'Hooks/useStore';
import './AppLayout.css';

const locationBasedClasses = {
  '/account/redeem': 'giftingHome',
  '/account/redeem/': 'giftingHome',
  '/account/review-and-redeem': 'giftingRedeem centerFrame',
};

const getPreSelectedPlanFromUrl = (pathname, routes) => {
  const routesArray = Object.values(routes);

  for (let i = 0; i < routesArray.length; i++) {
    const route = routesArray[i];

    const routeMatch = matchPath({ path: route, exact: true }, pathname);
    if (routeMatch) {
      return routeMatch.params.preSelectedPlan;
    }
  }
};

export const getDefaultClassNames = (pathname) => {
  return `defaultLayout ${locationBasedClasses[pathname] || 'centerFrame'}`;
};

export default function AppLayout({ children }) {
  const { pathname = '' } = useLocation();
  const { tealiumProductEnv } = useStore('appConfig.defaultConfig');
  const { routes } = useStore('appConfig');

  /* Have to get param path because don't have access to react router useParams params
  here because this component is above the routes
  
  disabling eslint rule since only want this to run once on first render
  */

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const preSelectedPlan = useMemo(() => getPreSelectedPlanFromUrl(pathname, routes), []);
  usePlans(preSelectedPlan, pathname);

  useEffect(() => {
    // call segment identify api
    identify();
    // load paypal SDK
    loadPaypalSDK();

    // call Heap identity
    // set Braze user
    const storedUserId = localStorage.getItem('ajs_user_id');
    const ajsUserId = storedUserId && JSON.parse(storedUserId);
    heapIdentify(ajsUserId);
    setBrazeUser(ajsUserId);

    // load tealium script
    loadTealium(tealiumProductEnv);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useScrollTop();
  useEmailCoupon();

  const showHeader = !pathname.startsWith(routes.invoice);
  const showFooter = !pathname.startsWith(routes.invoice);

  const layoutClassName = useMemo(() => getDefaultClassNames(pathname), [pathname]);

  return (
    <ErrorBoundary name="app layout">
      <div className={`fn-app ${layoutClassName}`}>
        <div>
          <DocHead path={pathname} />
          {showHeader && (
            <div className="headerContainer sticky">
              <Header />
            </div>
          )}
          <div className="bodyContainer" role="main">
            {children}
          </div>
        </div>
        {showFooter && <Footer path={pathname} />}
      </div>
      <Overlay />
    </ErrorBoundary>
  );
}
